import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react'
import { DragDropContext as DragDropContextOriginal, DragDropContextProps } from 'react-beautiful-dnd'

const supportedEvents = ['onBeforeCapture', 'onBeforeDragStart', 'onDragStart', 'onDragUpdate', 'onDragEnd'] as const

export type DragDropHander = Partial<Pick<DragDropContextProps, typeof supportedEvents[number]>>
type Full<T extends { [key: string]: unknown }> = {
  [Key in keyof T]-?: Exclude<T[Key], undefined>
}

const mergeHandlers = <T extends keyof DragDropHander>(
  handlers: DragDropHander[],
  eventName: T
): Exclude<DragDropHander[T], undefined> => {
  const result = (((...args: unknown[]) => {
    handlers.forEach((handler) => {
      const fn = handler[eventName] as (...args: unknown[]) => unknown
      fn && fn(...args)
    })
  }) as unknown) as Exclude<DragDropHander[T], undefined>
  return result
}

export const HandlerContext = React.createContext({
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  addHandler: (handler: DragDropHander) => (): void => undefined
})

const DragDropContext: React.FC = ({ children }) => {
  const [handlers, setHandlers] = useState<DragDropHander[]>([])

  const removeHandler = useCallback((handler: DragDropHander) => {
    setHandlers((handlers) => handlers.filter((x) => x !== handler))
  }, [])
  const addHandler = useCallback(
    (handler: DragDropHander): (() => void) => {
      setHandlers((handlers) => handlers.filter((x) => x !== handler).concat([handler]))
      return () => removeHandler(handler)
    },
    [removeHandler]
  )
  const mergedHandlers = useMemo<Full<DragDropHander>>(
    () => ({
      onBeforeCapture: mergeHandlers(handlers, 'onBeforeCapture'),
      onBeforeDragStart: mergeHandlers(handlers, 'onBeforeDragStart'),
      onDragStart: mergeHandlers(handlers, 'onDragStart'),
      onDragUpdate: mergeHandlers(handlers, 'onDragUpdate'),
      onDragEnd: mergeHandlers(handlers, 'onDragEnd')
    }),
    [handlers]
  )

  return (
    <HandlerContext.Provider value={useMemo(() => ({ addHandler }), [addHandler])}>
      <DragDropContextOriginal {...mergedHandlers}>{children}</DragDropContextOriginal>
    </HandlerContext.Provider>
  )
}

export const useDragDropHandler = (handler: DragDropHander): void => {
  const { addHandler } = useContext(HandlerContext)
  useEffect(() => {
    return addHandler(handler)
  }, [handler, addHandler])
}

/**
 * Returns the type of the currently dragged element.
 *
 * If no element is being dragged returns undefined
 */
export const useCurrentDragType = (): string | undefined => {
  const [currentDragType, setCurrentDragType] = useState<string>()
  useDragDropHandler(
    useMemo(
      () => ({
        onDragStart({ type }) {
          setCurrentDragType(type)
        },
        onDragEnd() {
          setCurrentDragType(undefined)
        }
      }),
      []
    )
  )
  return currentDragType
}

export default DragDropContext
